import React, { useMemo, useState } from 'react';
import { FilterIcon, ClockIcon } from 'lucide-react';
import type { HistoricalRecord, MetricType, TimeframeType } from '../../types';
import { Header } from '../layout/Header';
import { HistoryTable } from './HistoryTable';
import { getHistoricalRecords } from '../../data/mockData';
type EntityFilter = 'all' | 'division' | 'team' | 'loanOfficer';
type PeriodFilter = 'all' | 'weekly' | 'monthly';
type MetricFilter = 'all' | MetricType;
export function HistoryPage() {
  const allRecords = getHistoricalRecords();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [metricFilter, setMetricFilter] = useState<MetricFilter>('all');
  const [entityFilter, setEntityFilter] = useState<EntityFilter>('all');
  const filteredRecords = useMemo(() => {
    return allRecords.filter((record: HistoricalRecord) => {
      if (periodFilter !== 'all' && record.periodType !== periodFilter) return false;
      if (metricFilter !== 'all' && record.metric !== metricFilter) return false;
      if (entityFilter !== 'all' && record.entityType !== entityFilter) return false;
      return true;
    });
  }, [allRecords, periodFilter, metricFilter, entityFilter]);
  const achievedCount = filteredRecords.filter((r: HistoricalRecord) => r.achieved).length;
  const missedCount = filteredRecords.filter((r: HistoricalRecord) => !r.achieved).length;
  return <div className="min-h-screen" data-id="element-162">
      <Header title="Historical Records" subtitle="Review past goal achievement across all periods and entities" data-id="element-163" />

      <div className="p-6 lg:p-8 space-y-6" data-id="element-164">
        {/* Summary Stats */}
        <div className="flex items-center gap-6 flex-wrap" data-id="element-165">
          <div className="flex items-center gap-2" data-id="element-166">
            <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-167" />
            <span className="text-sm text-gray-600" data-id="element-168">
              <span className="font-semibold text-gray-900" data-id="element-169">
                {filteredRecords.length}
              </span>{' '}
              records
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-170">
            <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-171" />
            <span className="text-sm text-gray-600" data-id="element-172">
              <span className="font-semibold text-emerald-700" data-id="element-173">
                {achievedCount}
              </span>{' '}
              achieved
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-174">
            <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-175" />
            <span className="text-sm text-gray-600" data-id="element-176">
              <span className="font-semibold text-red-700" data-id="element-177">{missedCount}</span>{' '}
              missed
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-id="element-178">
          <div className="flex items-center gap-2 mb-3" data-id="element-179">
            <FilterIcon className="w-4 h-4 text-gray-400" data-id="element-180" />
            <span className="text-sm font-medium text-gray-700" data-id="element-181">Filters</span>
          </div>
          <div className="flex flex-wrap gap-4" data-id="element-182">
            <div data-id="element-183">
              <label htmlFor="filter-period" className="block text-xs font-medium text-gray-500 mb-1" data-id="element-184">
                Period Type
              </label>
              <select id="filter-period" value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-185">
                <option value="all" data-id="element-186">All Periods</option>
                <option value="weekly" data-id="element-187">Weekly</option>
                <option value="monthly" data-id="element-188">Monthly</option>
              </select>
            </div>

            <div data-id="element-189">
              <label htmlFor="filter-metric" className="block text-xs font-medium text-gray-500 mb-1" data-id="element-190">
                Metric
              </label>
              <select id="filter-metric" value={metricFilter} onChange={e => setMetricFilter(e.target.value as MetricFilter)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-191">
                <option value="all" data-id="element-192">All Metrics</option>
                <option value="softPull" data-id="element-193">Soft Pull</option>
                <option value="submitted" data-id="element-194">Submitted</option>
              </select>
            </div>

            <div data-id="element-195">
              <label htmlFor="filter-entity" className="block text-xs font-medium text-gray-500 mb-1" data-id="element-196">
                Entity Type
              </label>
              <select id="filter-entity" value={entityFilter} onChange={e => setEntityFilter(e.target.value as EntityFilter)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-197">
                <option value="all" data-id="element-198">All Entities</option>
                <option value="division" data-id="element-199">Division</option>
                <option value="team" data-id="element-200">Team</option>
                <option value="loanOfficer" data-id="element-201">Loan Officer</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <HistoryTable records={filteredRecords} data-id="element-202" />
      </div>
    </div>;
}